// frontend/src/pages/shopfloor/ShopFloorHome.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button,
  Container,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import { Build } from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useV2CutoverRedirect from './useV2CutoverRedirect';

export default function ShopFloorHome() {
  // After the v1→v2 cutover, the legacy shop-floor home forwards to v2.
  useV2CutoverRedirect('/manufacturing/v2/shop-floor');
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const [operations, setOperations] = useState([]);

  useEffect(() => {
    loadOperations();
  }, []);

  const loadOperations = async () => {
    try {
      const response = await manufacturingAPI.operations.list();
      const operationsList = response.data.results || response.data;
      
      // Sort operations properly: extract numbers and sort numerically, then alphabetically
      const sortedOperations = operationsList.sort((a, b) => {
        const aCode = a.operation_code;
        const bCode = b.operation_code;
        
        // Extract numbers from the operation codes
        const aMatch = aCode.match(/\d+/);
        const bMatch = bCode.match(/\d+/);
        
        // If both have numbers, compare numerically
        if (aMatch && bMatch) {
          const aNum = parseInt(aMatch[0], 10);
          const bNum = parseInt(bMatch[0], 10);
          
          if (aNum !== bNum) {
            return aNum - bNum;
          }
        }
        
        // If one has number and other doesn't, number comes first
        if (aMatch && !bMatch) return -1;
        if (!aMatch && bMatch) return 1;
        
        // Fallback to alphabetical comparison
        return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base' });
      });
      
      setOperations(sortedOperations);
    } catch (error) {
      console.error('Error loading operations:', error);
    }
  };

  if (!user) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'background.default'
      }}>
        <Card sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Build sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Shop Floor Access
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              sx={{ mt: 3 }}
              onClick={() => navigate('/dashboard')}
            >
              Login Required
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      py: 4
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
            <Build sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h3" fontWeight={700}>
                Shop Floor Terminal
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Select your operation station
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: alpha(theme.palette.common.white, 0.1),
              zIndex: 0
            }}
          />
        </Paper>

        {/* Operations Grid */}
        <Grid container spacing={3}>
          {/* All Operations Card - Always First */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                height: '100%',
                transition: 'all 0.3s ease',
                border: `2px solid ${theme.palette.primary.main}`,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                  borderColor: theme.palette.primary.dark,
                  bgcolor: alpha(theme.palette.primary.main, 0.1)
                }
              }} 
              onClick={() => navigate('/shop-floor/all')}
            >
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 1,
                  color: 'primary.main'
                }}>
                  <Build />
                  <Typography variant="h6" fontWeight={700}>
                    ALL
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600} gutterBottom color="primary">
                  All Operations
                </Typography>
                <Typography color="text.secondary">
                  View work orders for all operation stations
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Individual Operation Cards - Sorted */}
          {operations.map((operation) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={operation.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    borderColor: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }
                }} 
                onClick={() => navigate(`/shop-floor/${operation.operation_code}`)}
              >
                <CardContent>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1,
                    color: 'text.secondary'
                  }}>
                    <Build fontSize="small" />
                    <Typography variant="caption" fontWeight={600}>
                      OPERATION
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} gutterBottom color="primary">
                    {operation.operation_code}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {operation.name}
                  </Typography>
                  {operation.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {operation.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {operations.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center', mt: 4 }}>
            <Build sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No operations available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact your administrator to set up operation stations
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}